import React, { useState, useEffect } from 'react';
import { Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { produksjonAPI } from '@/services/api';
import { usePlanMalDialog, type PlanMalElement } from './plan-mal-editor/hooks/usePlanMalDialog';
import { usePlanMalDragDrop } from './plan-mal-editor/hooks/usePlanMalDragDrop';
import { MessageToast } from './plan-mal-editor/components/MessageToast';
import { PlanMalList } from './plan-mal-editor/components/PlanMalList';
import PlanElementDialog from '../production/PlanElementDialog';

interface Props {
  kategoriId: number;
  onSave: () => void;
}

const PlanMalEditor: React.FC<Props> = ({ kategoriId, onSave }) => {
  const [elements, setElements] = useState<PlanMalElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedHeaders, setExpandedHeaders] = useState<Set<number>>(new Set());

  const dialog = usePlanMalDialog();

  useEffect(() => {
    fetchPlanMal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoriId]);

  const fetchPlanMal = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await produksjonAPI.getPlanMal(kategoriId);
      setElements(data.planMal);
    } catch (err: any) {
      console.error('Fetch plan-mal error:', err);
      setError('Kunne ikke laste plan-mal');
    } finally {
      setLoading(false);
    }
  };

  const toggleHeader = (headerId: number) => {
    setExpandedHeaders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(headerId)) {
        newSet.delete(headerId);
      } else {
        newSet.add(headerId);
      }
      return newSet;
    });
  };

  const dragDrop = usePlanMalDragDrop({
    kategoriId,
    elements,
    setElements,
    expandedHeaders,
    setExpandedHeaders,
    onSave,
    onError: (msg) => {
      setError(msg);
      setTimeout(() => setError(null), 3000);
    },
    onSuccess: (msg) => {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    },
    fetchPlanMal,
  });

  const getNextRekkefølge = (parentId: number | null): number => {
    const siblings = elements.filter(el => {
      if (parentId === null) {
        return el.type === 'overskrift';
      }
      return el.parent_id === parentId;
    });
    return siblings.length;
  };

  const handleSave = async () => {
    try {
      setError(null);
      
      if (!dialog.formState.isValid()) {
        setError('Navn er påkrevd');
        return;
      }

      if (dialog.editingElement) {
        await produksjonAPI.updatePlanMalElement(kategoriId, dialog.editingElement.id, {
          navn: dialog.formState.navn.trim(),
          ...(dialog.dialogMode === 'hendelse' && { varighetMinutter: dialog.formState.varighet }),
          ...(dialog.dialogMode === 'musikk_placeholder' && { varighetMinutter: dialog.formState.varighet }),
          ...(dialog.dialogMode === 'overskrift' && { førProduksjonsstart: dialog.formState.førProduksjonsstart }),
          beskrivelse: dialog.formState.beskrivelse.trim() || undefined,
        });
        setSuccess('Innslag oppdatert');
      } else {
        const nextRekkefølge = getNextRekkefølge(dialog.currentParentId);
        await produksjonAPI.addPlanMalElement(kategoriId, {
          type: dialog.dialogMode,
          navn: dialog.formState.navn.trim(),
          ...(dialog.dialogMode === 'hendelse' && dialog.currentParentId !== null && { 
            varighetMinutter: dialog.formState.varighet,
            parentId: dialog.currentParentId 
          }),
          ...(dialog.dialogMode === 'musikk_placeholder' && dialog.currentParentId !== null && { 
            varighetMinutter: dialog.formState.varighet,
            parentId: dialog.currentParentId 
          }),
          ...(dialog.dialogMode === 'overskrift' && { førProduksjonsstart: dialog.formState.førProduksjonsstart }),
          rekkefølge: nextRekkefølge,
          beskrivelse: dialog.formState.beskrivelse.trim() || undefined,
        });
        setSuccess(`${dialog.dialogMode === 'overskrift' ? 'Overskrift' : dialog.dialogMode === 'musikk_placeholder' ? 'Musikk-placeholder' : 'Innslag'} lagt til`);
      }

      dialog.closeDialog();
      await fetchPlanMal();
      onSave();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.error || 'Kunne ikke lagre');
    }
  };

  const handleDelete = async (elementId: number) => {
    if (!window.confirm('Er du sikker på at du vil slette dette innslaget?')) {
      return;
    }

    try {
      setError(null);
      await produksjonAPI.removePlanMalElement(kategoriId, elementId);
      setSuccess('Innslag slettet');
      await fetchPlanMal();
      onSave();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Delete error:', err);
      setError('Kunne ikke slette innslag');
    }
  };

  const overskrifter = elements.filter(el => el.type === 'overskrift').sort((a, b) => a.rekkefølge - b.rekkefølge);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Laster plan-mal...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3">
        <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
        <h3 className="text-lg sm:text-xl font-bold text-foreground">Plan-mal</h3>
      </div>

      {error && (
        <MessageToast
          type="error"
          message={error}
          onDismiss={() => setError(null)}
        />
      )}
      {success && (
        <MessageToast
          type="success"
          message={success}
          onDismiss={() => setSuccess(null)}
        />
      )}

      <div className="flex gap-2">
        <Button
          onClick={() => dialog.openOverskriftDialog()}
          size="sm"
          className="gap-2 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-xs sm:text-sm">Legg til overskrift</span>
        </Button>
      </div>

      <div className="space-y-2 sm:space-y-3">
        <PlanMalList
          overskrifter={overskrifter}
          elements={elements}
          expandedHeaders={expandedHeaders}
          activeElement={dragDrop.activeElement}
          sensors={dragDrop.sensors}
          onToggleHeader={toggleHeader}
          onEditOverskrift={(element) => dialog.openEditDialog(element)}
          onDeleteOverskrift={(id) => handleDelete(id)}
          onEditHendelse={(element) => dialog.openEditDialog(element)}
          onDeleteHendelse={(id) => handleDelete(id)}
          onAddHendelse={(parentId) => dialog.openHendelseDialog(parentId)}
          onAddMusikkPlaceholder={(parentId) => dialog.openMusikkPlaceholderDialog(parentId)}
          onDragStart={dragDrop.handleDragStart}
          onDragOver={dragDrop.handleDragOver}
          onDragEnd={dragDrop.handleDragEnd}
          onDragCancel={dragDrop.handleDragCancel}
        />
      </div>

      <PlanElementDialog
        open={dialog.dialogOpen}
        mode={dialog.dialogMode}
        isEditing={!!dialog.editingElement}
        formState={dialog.formState}
        onSave={handleSave}
        onCancel={dialog.closeDialog}
      />
    </div>
  );
};

export default PlanMalEditor;
